import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface Goal {
  id: string;
  title: string;
  description?: string;
  category?: string;
  target_date?: string;
  target_value?: number;
  current_value: number;
  unit?: string;
  status: string;
  priority: number;
  color: string;
  total_milestones?: number;
  completed_milestones?: number;
}

interface Milestone {
  id: string;
  title: string;
  target_value?: number;
  is_completed: boolean;
  completed_date?: string;
}

const CATEGORIES = ['Health', 'Finance', 'Education', 'Travel', 'Home', 'Family', 'Career', 'Other'];
const COLORS = ['#667eea', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#6b7280'];

const FamilyGoals: React.FC = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [filter, setFilter] = useState<string>('active');
  const [newGoal, setNewGoal] = useState({
    title: '', description: '', category: 'Health', targetDate: '', 
    targetValue: '', unit: '', priority: 2, color: '#667eea'
  });
  const [newMilestone, setNewMilestone] = useState('');
  const [progressValue, setProgressValue] = useState('');

  useEffect(() => {
    fetchGoals();
  }, [filter]);

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const response = await apiService.getGoals(filter === 'all' ? undefined : filter);
      if (response.success) {
        setGoals(response.goals || []);
      }
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGoalDetails = async (goal: Goal) => {
    try {
      const response = await apiService.getGoal(goal.id);
      if (response.success) {
        setSelectedGoal(response.goal);
        setMilestones(response.milestones || []);
      }
    } catch (error) {
      console.error('Failed to fetch goal details:', error);
    }
  };

  const handleAddGoal = async () => {
    if (!newGoal.title.trim()) return;

    try {
      const response = await apiService.createGoal({
        title: newGoal.title,
        description: newGoal.description,
        category: newGoal.category,
        targetDate: newGoal.targetDate || undefined,
        targetValue: newGoal.targetValue ? parseFloat(newGoal.targetValue) : undefined,
        unit: newGoal.unit || undefined,
        priority: newGoal.priority,
        color: newGoal.color
      });
      if (response.success) {
        await fetchGoals();
        setNewGoal({ title: '', description: '', category: 'Health', targetDate: '', targetValue: '', unit: '', priority: 2, color: '#667eea' });
        setShowAddForm(false);
      }
    } catch (error) {
      console.error('Failed to create goal:', error);
    }
  };

  const handleAddMilestone = async () => {
    if (!selectedGoal || !newMilestone.trim()) return;

    try {
      const response = await apiService.addGoalMilestone(selectedGoal.id, { title: newMilestone });
      if (response.success) {
        setMilestones([...milestones, response.milestone]);
        setNewMilestone('');
      }
    } catch (error) {
      console.error('Failed to add milestone:', error);
    }
  };

  const handleToggleMilestone = async (milestone: Milestone) => {
    try {
      const response = await apiService.toggleMilestoneComplete(milestone.id);
      if (response.success) {
        setMilestones(milestones.map(m => m.id === milestone.id ? response.milestone : m));
      }
    } catch (error) {
      console.error('Failed to toggle milestone:', error);
    }
  };

  const handleAddProgress = async () => {
    if (!selectedGoal || !progressValue) return;

    try {
      const response = await apiService.addGoalProgress(selectedGoal.id, {
        valueAdded: parseFloat(progressValue)
      });
      if (response.success) {
        setSelectedGoal(response.goal);
        setGoals(goals.map(g => g.id === response.goal.id ? { ...g, current_value: response.goal.current_value } : g));
        setProgressValue('');
      }
    } catch (error) {
      console.error('Failed to add progress:', error);
    }
  };

  const handleUpdateStatus = async (goalId: string, status: string) => {
    try {
      await apiService.updateGoal(goalId, { status });
      await fetchGoals();
      if (selectedGoal?.id === goalId) {
        setSelectedGoal({ ...selectedGoal, status });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDeleteGoal = async (id: string) => {
    if (!window.confirm('Delete this goal? All milestones and progress will be lost.')) return;

    try {
      await apiService.deleteGoal(id);
      setGoals(goals.filter(g => g.id !== id));
      if (selectedGoal?.id === id) setSelectedGoal(null);
    } catch (error) {
      console.error('Failed to delete goal:', error);
    }
  };

  const getProgress = (goal: Goal) => {
    if (!goal.target_value) return 0;
    return Math.min(100, (goal.current_value / goal.target_value) * 100);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #667eea', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Family Goals</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Track and achieve your family's goals together</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          data-testid="new-goal-btn"
          style={{ padding: '10px 20px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
        >
          + New Goal
        </button>
      </div>

      {/* Usage Tips */}
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#fef3c7', 
        borderRadius: '10px', 
        marginBottom: '20px',
        border: '1px solid #fcd34d'
      }}>
        <div style={{ fontSize: '0.875rem', color: '#92400e', fontWeight: 600, marginBottom: '8px' }}>
          Tips for using Family Goals
        </div>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#78350f', fontSize: '0.8rem', lineHeight: 1.6 }}>
          <li>Track kids' clothing sizes, doctor visits, or gift ideas</li>
          <li>Set savings targets for vacations or home improvements</li>
          <li>Create milestones to break big goals into smaller steps</li>
          <li>Add progress updates to celebrate achievements together</li>
          <li>Use categories to organize goals by Health, Finance, Education, etc.</li>
        </ul>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {['active', 'completed', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '8px 16px',
              backgroundColor: filter === f ? '#667eea' : 'white',
              color: filter === f ? 'white' : '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '20px',
              cursor: 'pointer',
              fontWeight: 500,
              textTransform: 'capitalize'
            }}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600 }}>Create New Goal</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px' }}>Goal Title *</label>
              <input
                type="text"
                value={newGoal.title}
                onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                placeholder="e.g., Save for vacation"
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px' }}>Category</label>
              <select
                value={newGoal.category}
                onChange={(e) => setNewGoal({ ...newGoal, category: e.target.value })}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px' }}>Description</label>
              <textarea
                value={newGoal.description}
                onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                placeholder="Describe your goal..."
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', minHeight: '80px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px' }}>Target Value (optional)</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="number"
                  value={newGoal.targetValue}
                  onChange={(e) => setNewGoal({ ...newGoal, targetValue: e.target.value })}
                  placeholder="e.g., 5000"
                  style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <input
                  type="text"
                  value={newGoal.unit}
                  onChange={(e) => setNewGoal({ ...newGoal, unit: e.target.value })}
                  placeholder="Unit (e.g., $)"
                  style={{ width: '100px', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px' }}>Target Date</label>
              <input
                type="date"
                value={newGoal.targetDate}
                onChange={(e) => setNewGoal({ ...newGoal, targetDate: e.target.value })}
                style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px' }}>Color</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setNewGoal({ ...newGoal, color: c })}
                    style={{
                      width: '32px', height: '32px', borderRadius: '50%', backgroundColor: c,
                      border: newGoal.color === c ? '3px solid #1f2937' : '2px solid transparent',
                      cursor: 'pointer'
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button onClick={handleAddGoal} style={{ padding: '10px 20px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
              Create Goal
            </button>
            <button onClick={() => setShowAddForm(false)} style={{ padding: '10px 20px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Goals Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
        {goals.map(goal => {
          const progress = getProgress(goal);
          return (
            <div
              key={goal.id}
              onClick={() => fetchGoalDetails(goal)}
              style={{
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: selectedGoal?.id === goal.id ? `2px solid ${goal.color}` : '1px solid #e5e7eb',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: goal.color }} />
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>{goal.title}</h3>
                    {goal.category && (
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', padding: '2px 8px', backgroundColor: '#f3f4f6', borderRadius: '10px' }}>
                        {goal.category}
                      </span>
                    )}
                  </div>
                </div>
                {goal.status === 'completed' && <span style={{ color: '#22c55e', fontWeight: 700 }}>Done</span>}
              </div>

              {goal.target_value && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                    <span>{goal.current_value} / {goal.target_value} {goal.unit}</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progress}%`, backgroundColor: goal.color, borderRadius: '4px', transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#6b7280' }}>
                <span>
                  {goal.total_milestones ? `${goal.completed_milestones}/${goal.total_milestones} milestones` : 'No milestones'}
                </span>
                {goal.target_date && (
                  <span>Due: {new Date(goal.target_date).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {goals.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          No goals found. Create your first family goal!
        </div>
      )}

      {/* Goal Detail Modal */}
      {selectedGoal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onClick={() => setSelectedGoal(null)}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px', padding: '24px', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>{selectedGoal.title}</h2>
                {selectedGoal.description && <p style={{ margin: '8px 0 0 0', color: '#6b7280' }}>{selectedGoal.description}</p>}
              </div>
              <button onClick={() => setSelectedGoal(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>X</button>
            </div>

            {/* Progress */}
            {selectedGoal.target_value && (
              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 600 }}>Progress</span>
                  <span>{selectedGoal.current_value} / {selectedGoal.target_value} {selectedGoal.unit}</span>
                </div>
                <div style={{ height: '12px', backgroundColor: '#e5e7eb', borderRadius: '6px', overflow: 'hidden', marginBottom: '12px' }}>
                  <div style={{ height: '100%', width: `${getProgress(selectedGoal)}%`, backgroundColor: selectedGoal.color, borderRadius: '6px' }} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    value={progressValue}
                    onChange={(e) => setProgressValue(e.target.value)}
                    placeholder={`Add ${selectedGoal.unit || 'progress'}`}
                    style={{ flex: 1, padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                  />
                  <button onClick={handleAddProgress} style={{ padding: '8px 16px', backgroundColor: selectedGoal.color, color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}>
                    + Add
                  </button>
                </div>
              </div>
            )}

            {/* Milestones */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600 }}>Milestones</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {milestones.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: m.is_completed ? '#f0fdf4' : '#f9fafb', borderRadius: '8px' }}>
                    <button
                      onClick={() => handleToggleMilestone(m)}
                      style={{
                        width: '24px', height: '24px', borderRadius: '50%',
                        border: `2px solid ${m.is_completed ? '#22c55e' : '#d1d5db'}`,
                        backgroundColor: m.is_completed ? '#22c55e' : 'white',
                        cursor: 'pointer', color: 'white', fontWeight: 700, fontSize: '0.75rem'
                      }}
                    >
                      {m.is_completed && 'check'}
                    </button>
                    <span style={{ flex: 1, textDecoration: m.is_completed ? 'line-through' : 'none', color: m.is_completed ? '#6b7280' : '#1f2937' }}>
                      {m.title}
                    </span>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={newMilestone}
                    onChange={(e) => setNewMilestone(e.target.value)}
                    placeholder="Add milestone..."
                    style={{ flex: 1, padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddMilestone()}
                  />
                  <button onClick={handleAddMilestone} style={{ padding: '8px 16px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {selectedGoal.status === 'active' && (
                <button onClick={() => handleUpdateStatus(selectedGoal.id, 'completed')} style={{ padding: '10px 20px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                  Mark Complete
                </button>
              )}
              {selectedGoal.status === 'completed' && (
                <button onClick={() => handleUpdateStatus(selectedGoal.id, 'active')} style={{ padding: '10px 20px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                  Reopen
                </button>
              )}
              <button onClick={() => handleDeleteGoal(selectedGoal.id)} style={{ padding: '10px 20px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                Delete Goal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyGoals;
